<template>
    <div class="farm-list-table">
        <el-table
            v-if="operations.itens"
            :data="operations.itens"
            :empty-text="translate('no_data')"
            highlight-current-row
        >
            <el-table-column
                width="130px"
                prop="numeroContrato"
                :label="translate('contract_number')"
                label-class-name="transaction-list-table_label"
            />
            <el-table-column
                width="160px"
                prop="codigoTipoTransacao"
                :label="translate('transaction_type')"
                label-class-name="transaction-list-table_label"
            >
                <template v-slot:default="scope">
                    <div class="transaction-list_text">
                        {{ $t('contrato_rastreabilidade.messages.transaction_type.' + scope.row.codigoTipoTransacao.toLowerCase()) }}
                    </div>
                </template>
            </el-table-column>
            <el-table-column
                min-width="150px"
                prop="armazemOrigem"
                :label="translate('origin')"
                label-class-name="transaction-list-table_label"
            >
                <template v-slot:default="scope">
                    <div class="transaction-list_text">
                        {{ scope.row.armazemOrigem }}
                    </div>
                </template>
            </el-table-column>
            <el-table-column
                min-width="150px"
                prop="armazemDestino"
                :label="translate('destination')"
                label-class-name="transaction-list-table_label"
            >
                <template v-slot:default="scope">
                    <div class="transaction-list_text">
                        {{ scope.row.armazemDestino }}
                    </div>
                </template>
            </el-table-column>
            <el-table-column
                min-width="100px"
                align="right"
                prop="volumeOrigemTonFormatado"
                :formatter="formatNumber"
                :label="`${translate('outbound_volume')} (t)`"
                label-class-name="transaction-list-table_label"
            />
            <el-table-column
                min-width="100px"
                align="right"
                prop="volumeDestinoTonFormatado"
                :formatter="formatNumber"
                :label="`${translate('inbound_volume')} (t)`"
                label-class-name="transaction-list-table_label"
            />
            <el-table-column
                width="120px"
                prop="dataTransacao"
                :label="translate('report_delivery_date')"
                label-class-name="transaction-list-table_label"
            >
                <template v-slot:default="scope">
                    <div>
                        {{ formatDateTimeToString(scope.row.dataTransacao )[0] }}
                    </div>
                    <div class="table_second-row-label">
                        <i class="mdi mdi-clock-outline"></i>
                        {{ formatDateTimeToString(scope.row.dataTransacao )[1] }}
                    </div>
                </template>
            </el-table-column>
            <el-table-column
                width="120px"
                prop="codigoStatus"
                align="center"
                :label="translate('status')"
                label-class-name="transaction-list-table_label"
            >
                <template v-slot="scope">
                    <el-tag
                        :type="operationStatusColors[scope.row.codigoStatus].color"
                        style="text-wrap: nowrap"
                        size="mini"
                    >
                        {{
                            $t(
                                `contrato_rastreabilidade.messages.status.${
                                    operationStatusColors[scope.row.codigoStatus].label
                                }`
                            )
                        }}
                    </el-tag>
                </template>
            </el-table-column>
            <el-table-column
                :label="translate('actions')"
                align="center"
                label-class-name="transaction-list-table_label"
            >
                <template class="" v-slot:default="scope">
                    <el-dropdown
                        class="action-button"
                        trigger="click"
                        @click="scope"
                    >
                        <span class="el-dropdown-link">
                            <i class="mdi mdi-dots-vertical" />
                        </span>
                        <el-dropdown-menu v-slot="dropdown">
                            <el-dropdown-item
                                v-if="checkApprovalCredential() === true && checkIsPendingTransaction(scope.row)"
                                icon="el-icon-edit"
                                @click.native="openTransactionApproveModal(scope.row)"
                            >
                                {{ translate('action_options.analyze') }}
                            </el-dropdown-item>
                            <el-dropdown-item
                                icon="el-icon-map-location"
                                @click.native="openViewTerritoriesModal(scope.row)"
                            >
                                {{ translate('action_options.view_territories') }}
                            </el-dropdown-item>
                            <el-dropdown-item
                                v-if="checkApprovalCredential() === true && checkIsApprovedTransaction(scope.row)"
                                icon="el-icon-download"
                                @click.native="downloadContractFile(scope.row)"
                            >
                                {{ translate('action_options.download_document') }}
                            </el-dropdown-item>
                        </el-dropdown-menu>
                    </el-dropdown>
                </template>
            </el-table-column>
        </el-table>
        <div class="farm-list-table__pagination">
            <div class="total">
                <span
                    >{{ totalElements }}
                    {{
                        $tc(
                            'contrato_rastreabilidade.words.results',
                            totalElements
                        )
                    }}</span
                >
                <div style="margin: 0 20px"></div>
                <span
                    >{{
                        $t('contrato_rastreabilidade.words.views_by_page')
                    }}:</span
                >
                <el-pagination
                    @size-change="handleSizeChange"
                    :current-page="page"
                    :page-sizes="[10, 20, 30, 40]"
                    :page-size="size"
                    layout="sizes"
                    :total="totalElements"
                />
            </div>
            <div class="prev_next">
                <el-pagination
                    @current-change="handleCurrentChange"
                    :current-page="page"
                    :page-sizes="[10, 20, 30, 40]"
                    :page-size="size"
                    layout="prev, pager, next, jumper"
                    :total="totalElements"
                />
            </div>
        </div>
        <transaction-approve-modal
            v-if="selectedTransaction"
            :show="showTransactionApproveModal"
            :transaction="selectedTransaction"
            @close="showTransactionApproveModal = false"
            @confirm="handleConfirm"
        />
        <view-territories-modal
            v-if="showViewTerritoriesModal"
            :show="showViewTerritoriesModal"
            :transaction="selectedTransaction"
            @close="showViewTerritoriesModal = false"
        />
    </div>
</template>

<script>
import { mapState } from 'vuex';
import { operationStatusColors } from '@/modules/traceability-contract/utils';
import formatters from '@/utils/formatters';
import TransactionApproveModal from '@/modules/warehouse/views/pages/viewWarehouse/components/transaction-approve-modal/index.vue';
import ViewTerritoriesModal from '@/modules/warehouse/views/components/ViewTerritoriesModal.vue';
import RastreabilidadeTransacaoArmazemService from '@/services/RastreabilidadeTransacaoArmazemService';

export default {
    name: 'ReportDefault',
    props: {
        operations: {
            type: Object,
            default: () => {}
        }
    },
    components: {
        TransactionApproveModal,
        ViewTerritoriesModal
    },
    data() {
        return {
            operationStatusColors: operationStatusColors,
            formatters,
            showDialogContractFinish: false,
            currentContract: null,
            size: 10,
            page: 1,
            totalElements: this.operations.totalItens,
            showTransactionApproveModal: false,
            selectedTransaction: null,
            showViewTerritoriesModal: false
        };
    },
    computed: {
        ...mapState('traceabilityContractModule', ['filter']),
        ...mapState(['usuarioLogado'])
    },
    mounted() {
        this.totalElements = this.operations.totalItens ?? 0;
    },
    watch: {
        showDialogContractFinish(value) {
            if (!value) {
                this.$store.commit('traceabilityContractModule/RESET_FORM');
            }
        },
        'operations': {
            handler() {
                this.totalElements = this.operations.totalItens;
                this.size = this.filter.size;
                this.page = (this.filter.page ?? 0) + 1;
            },
            deep: true
        }
    },
    methods: {
        translate(key, params = {}) {
            const base = 'contrato_rastreabilidade.messages.main_list_table';
            return this.$t(`${base}.${key}`, params);
        },
        handleCurrentChange(page) {
            this.page = page;
            this.$store.commit('traceabilityContractModule/SET_FILTER', {
                ...this.filter,
                page: page - 1,
                size: this.size
            });
        },
        handleSizeChange(size) {
            this.page = 1;
            this.size = size;
            this.$store.commit('traceabilityContractModule/SET_FILTER', {
                ...this.filter,
                page: 0,
                size: size
            });
        },
        formatNumber(row, column, reportVolume, index) {
            return formatters.formatNumber(reportVolume);
        },
        formatDateTimeToString(dateTimeFromApi) {
            return formatters.splitApiDateTimeForTable(dateTimeFromApi, this.$i18n.locale);
        },
        checkApprovalCredential() {
            return this.usuarioLogado.armazemRastreabilidadeAccess;
        },
        checkIsPendingTransaction(row) {
            return row.codigoStatus === 'EM_TRANSITO' && this.usuarioLogado.idEmpresasFiltro.includes(row.empresaDestino);
        },
        checkIsApprovedTransaction(row) {
            return row.codigoStatus === 'APROVADA' && this.usuarioLogado.idEmpresasFiltro.includes(row.empresaDestino);
        },
        openTransactionApproveModal(row) {
            this.showTransactionApproveModal = true;
            this.selectedTransaction = row;
        },
        openViewTerritoriesModal(row) {
            this.showViewTerritoriesModal = true;
            this.selectedTransaction = row;
        },
        handleConfirm() {
            this.$store.commit('traceabilityContractModule/SET_FILTER', {
                page: 0
            });
        },
        async downloadContractFile(row) {
            try {
                const response = await RastreabilidadeTransacaoArmazemService.getTraceabilityContractPdf(row.id);
                const blob = new Blob([response.data], {
                    type: 'application/octet-stream'
                });
                const fileURL = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = fileURL;
                a.download = this.translate('contract_file_name_with_extension');
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(fileURL);
            } catch (error) {
                if (error.response?.data instanceof Blob) {
                    const text = await error.response.data.text();
                    const errorData = JSON.parse(text);
                    this.$notify.error({
                        message: this.translate('errors.' + errorData.message),
                        duration: 6000
                    });
                } else {
                    this.$notify.error({
                        message: this.translate('errors.error_message'),
                        duration: 6000
                    });
                }
            }
        }
    }
};
</script>

<style scoped lang="scss">
::v-deep .transaction-list-table_label {
    word-break: keep-all !important;
}
.transaction-list_text {
    word-break: keep-all !important;
}
.farm-list-table {
    .el-table {
        .cell {
            word-break: break-word !important;
        }

        .action-button {
            cursor: pointer;
        }
    }

    .farm-list-table__pagination {
        display: grid;
        grid-template-columns: 2fr auto;
        margin-top: 20px;
        @media (max-width: 768px) {
            grid-template-columns: 1fr;
        }

        .total {
            display: flex;
            align-items: center;
            @media (max-width: 768px) {
                flex-direction: column;
            }
        }

        .prev_next {
            display: flex;
            @media (max-width: 768px) {
                justify-content: center;
                margin-top: 20px;
            }
        }
    }
}
</style>
